import { apiClient, unwrap } from './client';

/**
 * SIMG-TERRAIN-2026-001 — Client API back-office terrain.
 *
 * Regroupe les endpoints exposés par le module Terrain de l'API SIMGROUP
 * (`/v1/admin/terrain-config`, `/v1/admin/intervenants`, etc.), utilisés
 * par les 4 pages de la section "Portail Terrain" du back-office siège.
 *
 * Les endpoints publics du portail terrain lui-même (`/terrain/session`,
 * `/terrain/declarations`, …) ne concernent PAS ce module.
 */

// ─── Types ─────────────────────────────────────────────────────

export interface TerrainConfig {
  contactTel:     string | null;
  contactLibelle: string | null;
  modifieLe:      string | null;
}

export interface Intervenant {
  id:        string;
  nom:       string;
  fonction:  string;
  telephone: string;
  actif:     boolean;
  /** BL-TERRAIN : personne désignée pour émettre des bons de livraison signés depuis le portail. */
  peutEmettreBl?: boolean;
  site:      { id: string; code: string; libelle: string };
  accesActif: {
    id:            string;
    expireLe:      string;
    dernierUsage:  string | null;
    appareilFige:  boolean;
    creeLe:        string;
  } | null;
  creeLe: string;
}

export interface Site {
  id:      string;
  code:    string;
  libelle: string;
  actif:   boolean;
  creeLe:  string;
}

export interface AireStockage {
  id:          string;
  siteId:      string;
  site:        { id: string; code: string; libelle: string };
  articleCode: string;
  capaciteKg:  string;   // BigInt sérialisé
  creeLe:      string;
}

export interface AccesLigne {
  id:              string;
  intervenant:     { id: string; nom: string; fonction: string; siteId: string };
  actif:           boolean;
  expireLe:        string;
  creeLe:          string;
  dernierUsage:    string | null;
  appareilFige:    boolean;
  revoqueLe:       string | null;
  motifRevocation: string | null;
}

export interface JournalEntry {
  id:           string;    // BigInt sérialisé
  horodatage:   string;
  resultat:     'ok' | 'expire' | 'revoque' | 'appareil_different' | 'inconnu';
  ip:           string | null;
  appareilHash: string | null;
  intervenant:  { id: string; nom: string } | null;
}

export interface JournalPage {
  total:  number;
  limit:  number;
  offset: number;
  rows:   JournalEntry[];
}

export interface LienGenere {
  secret:   string;
  expireLe: string;
  url:      string;
}

// ─── Fonctions ──────────────────────────────────────────────────

export const terrainAdminApi = {
  // Config runtime
  getConfig: (filiale: string) =>
    apiClient.get(`/terrain/config`, { params: { filiale } }).then(unwrap<TerrainConfig>),

  updateConfig: (patch: { contactTel?: string | null; contactLibelle?: string | null }) =>
    apiClient.put(`/admin/terrain-config`, patch).then(unwrap<TerrainConfig>),

  // Intervenants
  listIntervenants: (opts?: { siteId?: string; actif?: boolean }) =>
    apiClient.get(`/admin/intervenants`, {
      params: {
        ...(opts?.siteId ? { siteId: opts.siteId } : {}),
        ...(typeof opts?.actif === 'boolean' ? { actif: String(opts.actif) } : {}),
      },
    }).then(unwrap<Intervenant[]>),

  createIntervenant: (body: { nom: string; fonction: string; siteId: string; telephone: string }) =>
    apiClient.post(`/admin/intervenants`, body).then(unwrap<{ id: string }>),

  updateIntervenant: (id: string, patch: { nom?: string; fonction?: string; telephone?: string; actif?: boolean; peutEmettreBl?: boolean }) =>
    apiClient.patch(`/admin/intervenants/${id}`, patch).then(unwrap<{ id: string }>),

  // Accès
  generateAcces: (body: { intervenantId: string; motif?: string }) =>
    apiClient.post(`/admin/acces`, body).then(unwrap<LienGenere>),

  revokeAcces: (id: string, motif: string) =>
    apiClient.post(`/admin/acces/${id}/revoquer`, { motif }).then(unwrap<{ ok: true }>),

  listAcces: (opts?: { intervenantId?: string; actif?: boolean }) =>
    apiClient.get(`/admin/acces`, {
      params: {
        ...(opts?.intervenantId ? { intervenantId: opts.intervenantId } : {}),
        ...(typeof opts?.actif === 'boolean' ? { actif: String(opts.actif) } : {}),
      },
    }).then(unwrap<AccesLigne[]>),

  listJournal: (opts?: { limit?: number; offset?: number; resultat?: string; depuis?: string }) =>
    apiClient.get(`/admin/acces-journal`, { params: opts }).then(unwrap<JournalPage>),

  // Sites
  listSites: () => apiClient.get(`/admin/sites`).then(unwrap<Site[]>),

  createSite: (body: { code: string; libelle: string }) =>
    apiClient.post(`/admin/sites`, body).then(unwrap<{ id: string }>),

  updateSite: (id: string, patch: { libelle?: string; actif?: boolean }) =>
    apiClient.patch(`/admin/sites/${id}`, patch).then(unwrap<{ id: string }>),

  // Aires
  listAires: (siteId?: string) =>
    apiClient.get(`/admin/aires-stockage`, { params: siteId ? { siteId } : undefined }).then(unwrap<AireStockage[]>),

  createAire: (body: { siteId: string; articleCode: string; capaciteKg: string }) =>
    apiClient.post(`/admin/aires-stockage`, body).then(unwrap<{ id: string }>),

  updateAire: (id: string, patch: { capaciteKg?: string }) =>
    apiClient.patch(`/admin/aires-stockage/${id}`, patch).then(unwrap<{ id: string }>),

  // Déclarations (endpoints filiale existants, réutilisés pour la page Validations)
  listDeclarationsSoumises: () =>
    apiClient.get(`/filiale/declarations`, { params: { statut: 'soumise' } }).then(unwrap<any[]>),

  validerDeclaration: (id: string) =>
    apiClient.post(`/filiale/declarations/${id}/valider`).then(unwrap<any>),

  renvoyerDeclaration: (id: string, motif: string) =>
    apiClient.post(`/filiale/declarations/${id}/renvoyer`, { motif }).then(unwrap<any>),
};
